import React, { useCallback, useEffect, useRef, useState } from 'react';
import { inject } from 'mobx-react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Dialog, Typography, useMediaQuery } from '@material-ui/core';

import DialogTitle from '../common/dialog/DialogTitle';
import AddressSearchInput from './AddressSearchInput';
import AddressResultList from './AddressResultList';
import useAddressSearch from './useAddressSearch';
import { toSelection } from './addressFormat';
import { clearRecent, loadRecent, pushRecent, removeRecent } from './recentAddresses';
import T from './tokens';

const useStyles = makeStyles((theme) => ({
  paper: {
    width: 560,
    maxWidth: '100%',
    height: 'min(680px, 86vh)',
    margin: 16,
    borderRadius: T.radiusLg,
    boxShadow: T.dialogShadow,
    backgroundColor: T.surface,
  },
  paperMobile: {
    borderRadius: 0,
    backgroundColor: T.surface,
  },
  title: {
    padding: '18px 16px 10px',
    borderBottom: 'none',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    padding: '6px 24px 24px',
    [theme.breakpoints.down('sm')]: { padding: '4px 16px 16px' },
  },
  list: {
    flex: 1,
    minHeight: 0,
    marginTop: 14,
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'thin',
  },
}));

/**
 * 주소 검색 다이얼로그 — 다음 우편번호 위젯을 대신한다. 호출 방식은 이전과 같다.
 *  - type('to'|'from') 만 주면 공통 스토어(cmStore.setDaumAddress)에 주소·법정동코드를 넣는다
 *  - name + handleChange 를 주면 폼 이벤트 모양({ target: { name, value } })으로 돌려준다
 *  - onSelect(selection, type) 를 주면 그대로 넘긴다 (selection: toSelection 결과)
 */
function AddressSearchDialog({ open, type, name, handleClose, handleChange, onSelect, cmStore }) {
  const cs = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { setDaumAddress } = cmStore;
  const { query, items, status, error, hasMore, search, submit, loadMore, reset } = useAddressSearch();
  const [recent, setRecent] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const listRef = useRef(null);
  const listId = 'addressSearchList';

  useEffect(() => {
    if (open) {
      setRecent(loadRecent());
      setActiveIndex(-1);
    } else {
      reset();
    }
  }, [open, reset]);

  useEffect(() => { setActiveIndex(-1); }, [items]);

  const close = useCallback(() => handleClose(), [handleClose]);

  const choose = useCallback((item) => {
    const selection = toSelection(item);
    if (!selection.address) return;
    setRecent(pushRecent(item));
    if (onSelect) onSelect(selection, type);
    else if (name && handleChange) handleChange({ target: { name, value: selection.address } });
    else setDaumAddress(type, selection.address, selection.bcode);
    close();
  }, [onSelect, name, handleChange, setDaumAddress, type, close]);

  // 방향키로 결과 이동, Enter 로 선택 — 처리했으면 true 를 돌려 입력창의 기본 Enter(검색)를 막는다
  const handleKeyDown = (e) => {
    if (!items.length) return false;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      const next = Math.min(items.length - 1, Math.max(0, activeIndex + delta));
      setActiveIndex(next);
      const el = document.getElementById(`${listId}-${next}`);
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
      return true;
    }
    if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      choose(items[activeIndex]);
      return true;
    }
    return false;
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullScreen={isMobile}
      maxWidth={false}
      classes={{ paper: isMobile ? cs.paperMobile : cs.paper }}
      aria-labelledby="addressSearchTitle"
    >
      <DialogTitle id="addressSearchTitle" onClose={close} className={cs.title}>
        <Typography variant="body1" color="textPrimary" align="center"><b>주소 검색</b></Typography>
      </DialogTitle>
      <div className={cs.body}>
        <AddressSearchInput
          value={query}
          onChange={search}
          onSubmit={submit}
          onClear={() => search('')}
          onKeyDown={handleKeyDown}
          loading={status === 'loading' && !items.length}
          autoFocus
        />
        <div className={cs.list} ref={listRef}>
          <AddressResultList
            listId={listId}
            status={status}
            items={items}
            query={query}
            hasMore={hasMore}
            error={error}
            activeIndex={activeIndex}
            onSelect={choose}
            onLoadMore={loadMore}
            recent={recent}
            onSelectRecent={choose}
            onRemoveRecent={(id) => setRecent(removeRecent(id))}
            onClearRecent={() => setRecent(clearRecent())}
          />
        </div>
      </div>
    </Dialog>
  );
}

export default inject('cmStore')(AddressSearchDialog);
